import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { progressService, type UpdateProgressRequest } from '@/services'
import { getErrorMessage } from '@/utils/error'
import { useToast } from './use-toast'

export const useProgressStats = () => {
  return useQuery({
    queryKey: ['progress', 'stats'],
    queryFn: progressService.getStats,
  })
}

export const useEpisodeProgress = (episodeId: number) => {
  return useQuery({
    queryKey: ['progress', 'episode', episodeId],
    queryFn: () => progressService.getEpisodeProgress(episodeId),
    enabled: !!episodeId,
  })
}

export const useUpdateProgress = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: UpdateProgressRequest) => progressService.updateProgress(data),
    onSuccess: (data) => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['progress'] })
      queryClient.invalidateQueries({ queryKey: ['progress', 'episode', data.episodeId] })

      if (data.completed) {
        toast({
          title: 'Episódio concluído! 🎉',
          description: 'Continue assim, você está indo muito bem!',
        })
      }
    },
    onError: (error: unknown) => {
      toast({
        title: 'Erro ao salvar progresso',
        description: getErrorMessage(error),
        variant: 'destructive',
      })
    },
  })
}

export const useCourseProgress = (courseId: number | undefined) => {
  return useQuery({
    queryKey: ['progress', 'course', courseId],
    queryFn: () => progressService.getCourseProgress(courseId!),
    enabled: !!courseId,
    staleTime: 1000 * 60 * 5, // 5 minutos
  })
}

export const useActivityHeatmap = (year?: number) => {
  return useQuery({
    queryKey: ['progress', 'heatmap', year || new Date().getFullYear()],
    queryFn: () => progressService.getHeatmapData(year),
    staleTime: 1000 * 60 * 60, // 1 hora (dados históricos mudam pouco)
    gcTime: 1000 * 60 * 60 * 24, // 24 horas
  })
}
